import styled from "@emotion/styled";
import { useEffect, useState } from "react";

import { MDSDialogSlotName, MDSModalDialog, MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import {
  CommercialPlanKey,
  type CommercialCatalogSnapshot,
  type CommercialMeterKey,
  type CommercialPlanDefinition,
  type CommercialPlanSnapshot,
} from "@/modules/commercial-plans";
import { CommercialPlanSelector } from "./commercial-plan-selector";
import { getCommercialPlanSelection } from "./plan-selection";

interface CommercialPlanSelectorModalProps {
  actionIsLoading?: boolean;
  catalog: CommercialCatalogSnapshot;
  detail?: string;
  getActionLabel?: (plan: CommercialPlanDefinition) => string;
  isPlanAvailable?: (plan: CommercialPlanDefinition) => boolean;
  isOpen: boolean;
  meterKey?: CommercialMeterKey | null;
  mode?: "all" | "focused";
  onChoosePlan: (plan: CommercialPlanDefinition) => void;
  onClose: () => void;
  onSecondaryAction?: () => void;
  onSelectedPlanKeyChange?: (planKey: CommercialPlanKey) => void;
  onShowAllPlans?: () => void;
  secondaryActionLabel?: string;
  snapshot: CommercialPlanSnapshot;
  title: string;
}

export const CommercialPlanSelectorModal = ({
  actionIsLoading = false,
  catalog,
  detail,
  getActionLabel,
  isOpen,
  isPlanAvailable,
  meterKey = null,
  mode = "focused",
  onChoosePlan,
  onClose,
  onSecondaryAction,
  onSelectedPlanKeyChange,
  onShowAllPlans,
  secondaryActionLabel,
  snapshot,
  title,
}: CommercialPlanSelectorModalProps) => {
  const selection = getCommercialPlanSelection({ catalog, meterKey, mode, snapshot });
  const [selectedPlanKey, setSelectedPlanKey] = useState<CommercialPlanKey>(
    selection.initialSelectedPlanKey
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedPlanKey(selection.initialSelectedPlanKey);
    }
  }, [isOpen, selection.initialSelectedPlanKey]);

  return (
    <ResponsivePlanModal isOpen={isOpen} handleClose={onClose} fluidWidth>
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        {title}
      </MDSText>
      <PlanModalContent slot={MDSDialogSlotName.Content}>
        {detail ? (
          <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
            {detail}
          </MDSText>
        ) : null}
        <CommercialPlanSelector
          actionIsLoading={actionIsLoading}
          currentPlanKey={snapshot.currentPlan.key}
          getActionLabel={getActionLabel}
          isPlanAvailable={isPlanAvailable}
          memProTrialIsAvailable={snapshot.memProTrialEligible === true}
          onChoosePlan={onChoosePlan}
          onSecondaryAction={onSecondaryAction}
          onSelectPlan={planKey => {
            setSelectedPlanKey(planKey);
            onSelectedPlanKeyChange?.(planKey);
          }}
          onShowAllPlans={mode === "focused" ? onShowAllPlans : undefined}
          plans={selection.visiblePlans}
          recommendedPlanKey={selection.recommendedPlanKey}
          selectedPlanKey={selectedPlanKey}
          secondaryActionLabel={secondaryActionLabel}
          showComparisonToggle={mode === "all"}
        />
      </PlanModalContent>
    </ResponsivePlanModal>
  );
};

const ResponsivePlanModal = styled(MDSModalDialog)`
  .mds-dialog {
    width: min(1180px, calc(100vw - 40px));
    max-width: calc(100vw - 40px);
    max-height: calc(100vh - 40px);
  }

  [data-mds-name="dialog-body"] {
    overflow-x: hidden;
  }

  @media (max-width: 700px) {
    .mds-dialog {
      width: calc(100vw - 24px);
      max-width: calc(100vw - 24px);
      max-height: calc(100vh - 24px);
    }
  }
`;

const PlanModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;
